import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Card, CardContent } from '../../../components/ui';
import Button from '../../../components/ui/Button';
import ConfirmStatusDialog from './ConfirmStatusDialog';
import type { StoreSearchResult, StoreLiveStatus } from '../../../types/api.types';

interface StoreListSectionProps {
    stores: StoreSearchResult[];
    updatingStoreIds: string[];
    onStatusChange: (storeId: string, newStatus: StoreLiveStatus) => void;
    onOpenSettings: (store: StoreSearchResult) => void;
}

const STATUS_OPTIONS = [
    { value: 'Active' as const, label: 'Live' },
    { value: 'Trial' as const, label: 'Trial' },
    { value: 'Inactive' as const, label: 'Inactive' },
];

const STATUS_BADGE: Record<StoreLiveStatus, { dot: string; text: string; badge: string }> = {
    Active: {
        dot: 'bg-[#22c55e]',
        text: 'text-[#15803d]',
        badge: 'bg-[#f0fdf4] border-[#bbf7d0] text-[#15803d]',
    },
    Trial: {
        dot: 'bg-[#f59e0b]',
        text: 'text-[#92400e]',
        badge: 'bg-[#fffbeb] border-[#fde68a] text-[#92400e]',
    },
    Inactive: {
        dot: 'bg-[#94a3b8]',
        text: 'text-[#64748b]',
        badge: 'bg-[#f8fafc] border-[#e2e8f0] text-[#64748b]',
    },
};

function getEffectiveStatus(store: StoreSearchResult): StoreLiveStatus {
    return store.storeStatus ?? 'Trial';
}

export default function StoreListSection({
    stores,
    updatingStoreIds,
    onStatusChange,
    onOpenSettings,
}: StoreListSectionProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const [confirmDialog, setConfirmDialog] = useState<{
        isOpen: boolean;
        store: StoreSearchResult | null;
        newStatus: StoreLiveStatus;
    }>({ isOpen: false, store: null, newStatus: 'Active' });

    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!openDropdownId) return;
        function handleClickOutside(e: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpenDropdownId(null);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdownId]);

    const filteredStores = useMemo(() => {
        if (!searchQuery.trim()) return stores;
        const query = searchQuery.toLowerCase().trim();
        return stores.filter((store) => store.name.toLowerCase().includes(query));
    }, [stores, searchQuery]);

    const handleStatusSelect = useCallback(
        (store: StoreSearchResult, newStatus: StoreLiveStatus) => {
            setOpenDropdownId(null);
            const currentStatus = getEffectiveStatus(store);
            if (newStatus === currentStatus) return;
            setConfirmDialog({ isOpen: true, store, newStatus });
        },
        []
    );

    const handleConfirm = useCallback(() => {
        if (confirmDialog.store) {
            onStatusChange(confirmDialog.store._id, confirmDialog.newStatus);
        }
        setConfirmDialog({ isOpen: false, store: null, newStatus: 'Active' });
    }, [confirmDialog, onStatusChange]);

    const handleCancel = useCallback(() => {
        setConfirmDialog({ isOpen: false, store: null, newStatus: 'Active' });
    }, []);

    if (stores.length === 0) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="text-center">
                        <p className="text-sm text-[#64748b]">No stores found for this brand.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardContent className="py-4">
                    {/* Header + Search */}
                    <div className="flex items-center justify-between mb-4 gap-4">
                        <h3 className="text-sm font-semibold text-[#1e293b] flex-shrink-0">
                            Stores ({filteredStores.length}
                            {filteredStores.length !== stores.length ? ` of ${stores.length}` : ''})
                        </h3>
                        <div className="relative max-w-xs w-full">
                            <svg
                                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94a3b8]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                            <input
                                type="text"
                                placeholder="Filter stores..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e2e8f0] rounded-lg bg-white text-[#1e293b] placeholder-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#6e64f0]/20 focus:border-[#6e64f0] transition-colors"
                            />
                        </div>
                    </div>

                    {/* Store List */}
                    {filteredStores.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-sm text-[#64748b]">
                                No stores match &ldquo;{searchQuery}&rdquo;
                            </p>
                        </div>
                    ) : (
                        <div className="divide-y divide-[#e2e8f0]" ref={dropdownRef}>
                            {filteredStores.map((store) => {
                                const isUpdating = updatingStoreIds.includes(store._id);
                                const effectiveStatus = getEffectiveStatus(store);
                                const styles = STATUS_BADGE[effectiveStatus];
                                const isOpen = openDropdownId === store._id;

                                return (
                                    <div
                                        key={store._id}
                                        className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                                    >
                                        {/* Store icon + name */}
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-8 h-8 rounded-lg bg-[#f1f5f9] flex items-center justify-center flex-shrink-0">
                                                <svg
                                                    className="w-4 h-4 text-[#64748b]"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                                    />
                                                </svg>
                                            </div>
                                            <span className="text-sm font-medium text-[#1e293b] truncate">
                                                {store.name}
                                            </span>
                                        </div>

                                        {/* Right side: settings button + status badge */}
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                        <Button
                                            variant="primary"
                                            size="medium"
                                            onClick={() => onOpenSettings(store)}
                                        >
                                            <svg
                                                className="w-4 h-4 mr-2"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                            </svg>
                                            Settings
                                        </Button>

                                        {/* Status badge + dropdown */}
                                        {isUpdating ? (
                                            <div className="flex items-center gap-2 px-3 py-1.5">
                                                <span className="w-3.5 h-3.5 border-2 border-[#94a3b8] border-t-transparent rounded-full animate-spin" />
                                                <span className="text-xs text-[#94a3b8]">Updating…</span>
                                            </div>
                                        ) : (
                                            <div className="relative">
                                                <button
                                                    onClick={() =>
                                                        setOpenDropdownId(isOpen ? null : store._id)
                                                    }
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${styles.badge} hover:opacity-80`}
                                                >
                                                    <span
                                                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${styles.dot}`}
                                                    />
                                                    {STATUS_OPTIONS.find(
                                                        (o) => o.value === effectiveStatus
                                                    )?.label ?? effectiveStatus}
                                                    <svg
                                                        className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </svg>
                                                </button>

                                                {isOpen && (
                                                    <div className="absolute right-0 mt-1.5 w-36 bg-white rounded-xl border border-[#e2e8f0] shadow-lg z-10 py-1 overflow-hidden">
                                                        {STATUS_OPTIONS.map(({ value, label }) => {
                                                            const isSelected =
                                                                effectiveStatus === value;
                                                            const optStyles = STATUS_BADGE[value];
                                                            return (
                                                                <button
                                                                    key={value}
                                                                    onClick={() =>
                                                                        handleStatusSelect(
                                                                            store,
                                                                            value
                                                                        )
                                                                    }
                                                                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors hover:bg-[#f8fafc] ${
                                                                        isSelected
                                                                            ? optStyles.text
                                                                            : 'text-[#475569]'
                                                                    }`}
                                                                >
                                                                    <span
                                                                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${optStyles.dot}`}
                                                                    />
                                                                    {label}
                                                                    {isSelected && (
                                                                        <svg
                                                                            className="w-3 h-3 ml-auto"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                strokeWidth={2.5}
                                                                                d="M5 13l4 4L19 7"
                                                                            />
                                                                        </svg>
                                                                    )}
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            <ConfirmStatusDialog
                isOpen={confirmDialog.isOpen}
                storeName={confirmDialog.store?.name ?? ''}
                currentStatus={getEffectiveStatus(confirmDialog.store ?? { _id: '', name: '' })}
                newStatus={confirmDialog.newStatus}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </>
    );
}
